import { DependencyInjectId } from "@@/package/const/DependencyInjectId";
import { type ApplicationContextInterface } from "@@/package/application/applicationContext";
import OrganizationApi from "~~/package/apis/organization/OrganizationApi";
import {
    OrganizationProSkillManagerInfoModel
} from "~~/package/mapper/organization/model/OrganizationProSkillManagerInfoModel";
import type CustomResponse from "~~/package/types/ResponseType";
import { OrganizationSettingsInfoModel } from "~~/package/mapper/organization/model/OrganizationSettingsInfoModel";
import { PageModel } from "~~/package/mapper/common/model/PageModel";
import { OrganizationMemberModel } from "~~/package/mapper/organization/model/OrganizationMemberModel";
import OrganizationInviteResponse from "~~/package/apis/organization/response/OrganizationInviteResponse";
import { OrganizationInviteMemberModel } from "~~/package/mapper/organization/model/OrganizationInviteMemberModel";
import { UserV2Type } from "~~/package/mapper/user/model/UserType";
import { MemberMenuAccessRole } from "~~/package/mapper/organization/model/OrganizationMemeberTypes";
import type OrganizationAcceptInviteMemberResponse
    from '~~/package/apis/organization/response/OrganizationAcceptInviteMemberResponse';

export default class OrganizationMapper {
    protected organizationApi: OrganizationApi;

    constructor(context: ApplicationContextInterface) {
        this.organizationApi = context.getService<OrganizationApi>(DependencyInjectId.OrganizationApi);
    }

    public async getOrganizationProSkillManagerInfo(): Promise<OrganizationProSkillManagerInfoModel> {
        const insightsResponse: CustomResponse<OrganizationProSkillManagerInfoModel> = (await this.organizationApi.getOrganizationProSkillManagerInfo()).data;
        return OrganizationProSkillManagerInfoModel.create(insightsResponse.data);
    }

    public async getOrganizationSettingsInfo(): Promise<OrganizationSettingsInfoModel> {
        const insightsResponse: CustomResponse<OrganizationSettingsInfoModel> = (await this.organizationApi.getOrganizationSettingsInfo()).data;
        return OrganizationSettingsInfoModel.create(insightsResponse.data);
    }

    public async getOrganizationMember(page: number, size: number, sort?: string, keyword?: string): Promise<PageModel<OrganizationMemberModel>> {
        const insightsResponse: CustomResponse<PageModel<OrganizationMemberModel>> = (await this.organizationApi.getOrganizationMember(page, size, sort, keyword)).data;
        const pageData = PageModel.create(insightsResponse.data);
        pageData.data = pageData.data.map(member => OrganizationMemberModel.create(member));
        return pageData;
    }

    public async updateOrganizationSettingsInfo(name: string, size: string): Promise<OrganizationSettingsInfoModel> {
        const courseResponse: CustomResponse<OrganizationSettingsInfoModel> = (await this.organizationApi.updateOrganizationSettingsInfo(name, size)).data;
        return courseResponse.data;
    }

    public async deleteMyOrganization(): Promise<boolean> {
        const response: CustomResponse<boolean> = (await this.organizationApi.deleteMyOrganization()).data;
        return response.data;
    }

    public async createOrganization(name: string, size: string): Promise<boolean> {
        const userResponseData: CustomResponse<boolean> = (await this.organizationApi.createOrganization(name, size)).data;
        return userResponseData.data;
    }

    public async inviteOrganizationMembers(invitations: OrganizationInviteMemberModel[]): Promise<OrganizationInviteResponse> {
        const userResponseData: CustomResponse<OrganizationInviteResponse> = (await this.organizationApi.inviteOrganizationMembers(invitations)).data;
        return userResponseData.data;
    }

    public async updateOrganizationMemberRole(userUuid: string, organizationRole: UserV2Type, menuAccessRoles?: MemberMenuAccessRole[]): Promise<boolean> {
        const userResponseData: CustomResponse<boolean> = (await this.organizationApi.updateOrganizationMemberRole(userUuid, organizationRole, menuAccessRoles)).data;
        return userResponseData.data;
    }

    public async deleteOrganizationMember(userUuids: string[]): Promise<boolean> {
        const response: CustomResponse<boolean> = (await this.organizationApi.deleteOrganizationMember(userUuids)).data;
        return response.data;
    }

    public async acceptInviteMember(userUuid: string, orgUuid: string): Promise<OrganizationAcceptInviteMemberResponse> {
        const userResponseData: CustomResponse<OrganizationAcceptInviteMemberResponse> = (await this.organizationApi.acceptInviteMember(userUuid, orgUuid)).data;
        return userResponseData.data;
    }
}
